/**
 * Status labels per domain (German UI copy). Keys mirror the backend enums.
 * `tone` maps to StatusBadge variants: success | warning | destructive | info | neutral.
 * @typedef {{ label: string, tone: 'success'|'warning'|'destructive'|'info'|'neutral' }} StatusMeta
 */

/** @type {Record<string, StatusMeta>} */
export const METER_READING_STATUS = {
  open: { label: 'Offen', tone: 'warning' },
  read: { label: 'Abgelesen', tone: 'success' },
  /** API value after closing the year (MeterReading::CLOSED) */
  closed: { label: 'Ohne Ablesung', tone: 'neutral' },
  not_read: { label: 'Ohne Ablesung', tone: 'neutral' },
};

/** @type {Record<string, StatusMeta>} */
export const DOCUMENT_CHECK_STATUS = {
  created: { label: 'Angelegt', tone: 'neutral' },
  filled: { label: 'Befüllt', tone: 'info' },
  released: { label: 'Freigegeben', tone: 'info' },
  confirmed: { label: 'Bestätigt', tone: 'success' },
  owners_notified: { label: 'Eigentümer informiert', tone: 'success' },
};

/** @type {Record<string, StatusMeta>} */
export const IMPORT_RUN_STATUS = {
  queued: { label: 'Wartend', tone: 'neutral' },
  processing: { label: 'Läuft', tone: 'info' },
  completed: { label: 'Erfolgreich', tone: 'success' },
  pending: { label: 'Wartend', tone: 'neutral' },
  running: { label: 'Läuft', tone: 'info' },
  succeeded: { label: 'Erfolgreich', tone: 'success' },
  partial: { label: 'Teilweise', tone: 'warning' },
  failed: { label: 'Fehlgeschlagen', tone: 'destructive' },
};

/** @type {Record<string, StatusMeta>} */
export const OUTBOUND_MESSAGE_STATUS = {
  queued: { label: 'In Warteschlange', tone: 'neutral' },
  sending: { label: 'Wird versendet', tone: 'info' },
  sent: { label: 'Versendet', tone: 'success' },
  failed: { label: 'Fehlgeschlagen', tone: 'destructive' },
};

/** @type {Record<string, StatusMeta>} announcement_dispatches.status */
export const ANNOUNCEMENT_DISPATCH_STATUS = {
  queued: { label: 'In Warteschlange', tone: 'neutral' },
  processing: { label: 'Wird versendet', tone: 'info' },
  completed: { label: 'Versendet', tone: 'success' },
  failed: { label: 'Fehlgeschlagen', tone: 'destructive' },
};

/** @type {Record<string, StatusMeta>} */
export const MESSAGE_THREAD_STATUS = {
  open: { label: 'Offen', tone: 'warning' },
  in_progress: { label: 'In Bearbeitung', tone: 'info' },
  closed: { label: 'Erledigt', tone: 'success' },
};

/** @type {Record<string, StatusMeta>} users.status */
export const USER_STATUS = {
  active: { label: 'Aktiv', tone: 'success' },
  invited: { label: 'Eingeladen', tone: 'info' },
  locked: { label: 'Gesperrt', tone: 'destructive' },
};

/** @type {Record<string, StatusMeta>} migration_runs.status */
export const MIGRATION_RUN_STATUS = {
  running: { label: 'Läuft', tone: 'info' },
  completed: { label: 'Abgeschlossen', tone: 'success' },
  failed: { label: 'Fehlgeschlagen', tone: 'destructive' },
};

/** @type {Record<string, StatusMeta>} proxy authorizations (derived from `is_confirmed` of the API) */
export const PROXY_AUTHORIZATION_STATUS = {
  confirmed: { label: 'Bestätigt', tone: 'success' },
  pending: { label: 'Code ausstehend', tone: 'warning' },
};

/** @type {Record<string, StatusMeta>} tenancies.status */
export const TENANCY_STATUS = {
  current: { label: 'Aktuell', tone: 'success' },
  past: { label: 'Beendet', tone: 'neutral' },
};

export const STATUS_MAPS = {
  meterReading: METER_READING_STATUS,
  documentCheck: DOCUMENT_CHECK_STATUS,
  importRun: IMPORT_RUN_STATUS,
  outboundMessage: OUTBOUND_MESSAGE_STATUS,
  announcementDispatch: ANNOUNCEMENT_DISPATCH_STATUS,
  messageThread: MESSAGE_THREAD_STATUS,
  user: USER_STATUS,
  migrationRun: MIGRATION_RUN_STATUS,
  tenancy: TENANCY_STATUS,
  proxyAuthorization: PROXY_AUTHORIZATION_STATUS,
};

/**
 * @param {keyof typeof STATUS_MAPS} domain
 * @param {string} status
 * @returns {StatusMeta}
 */
export function getStatusMeta(domain, status) {
  return STATUS_MAPS[domain]?.[status] ?? { label: status ?? '–', tone: 'neutral' };
}

/** Role labels (German) */
export const ROLE_LABELS = {
  'super-admin': 'Super-Admin',
  admin: 'Administrator',
  staff: 'Verwaltung',
  'staff-limited': 'Verwaltung (eingeschränkt)',
  owner: 'Eigentümer',
  investor: 'Mandant',
  auditor: 'Kassenprüfer',
  'meter-reader': 'Ableser',
};
